import { FC, useEffect, useMemo } from 'react';
import { Callout, SelectBox, SelectBoxItem, Text, TextInput } from '@tremor/react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { EntityType, OrganizationWithWorkspaces } from '@/types/Organization';
import regions from '@/assets/csv/regions.csv';
import { GeneralModalProps } from '@/types/modals';
import { ModalWrapper } from '../ModalWrapper/ModalWrapper';
import { useFetcherWithUtils } from '@/hooks/useFetcherWithUtils';
import { assert } from '@/utils/asserts';
import { ModalBottomActions } from '../ModalBottomActions/ModalBottomActions';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FetcherEmptyResponse, PermissionFromStore } from '@/types/types';
import { hasPermissionMessageWithPolicy } from '@/auth/hasPermission';
import { OrganizationPolicyData } from '@/auth/types';

interface BaseProps extends GeneralModalProps {
  entityType: EntityType;
}

interface OrganizationProps extends BaseProps {
  entityType: 'organizations';
  organizationId?: never;
  organization_max_workspaces?: never;
  organizationWorkspacesNumber?: never;
  localSelectedOrganization?: never;
  localSelectedOrganizationPermissions?: never;
}

interface WorkspaceProps extends BaseProps {
  entityType: 'workspaces';
  organizationId: string;
  organization_max_workspaces: string | undefined;
  organizationWorkspacesNumber: number;
  localSelectedOrganization: OrganizationWithWorkspaces | null;
  localSelectedOrganizationPermissions: PermissionFromStore | null;
}

type Props = OrganizationProps | WorkspaceProps;

function handleClose(
  entityType: EntityType,
  searchParams: string,
  setClose: () => void,
  setSearchParams: ReturnType<typeof useSearchParams>[1],
  organizationId?: string,
) {
  if (entityType === 'organizations') {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('priceId');
    if (organizationId) newParams.set('newlyCreatedOrganizationId', organizationId);
    setSearchParams(newParams);
  } else {
    setClose();
  }
}

export const NewEntityModal: FC<Props> = ({ isOpen, setClose, isClossable, ...rest }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  return (
    <ModalWrapper
      className="w-fit h-fit"
      isOpen={isOpen}
      shouldCloseOnEsc={isClossable}
      shouldCloseOnOverlayClick={isClossable}
      onRequestClose={() =>
        handleClose(rest.entityType, searchParams.toString(), setClose, setSearchParams)
      }
    >
      <NewEntityModalContent setClose={setClose} {...rest} />
    </ModalWrapper>
  );
};

const NewEntityModalContent: FC<Omit<Props, 'isOpen'>> = ({
  setClose,
  entityType,
  organizationId,
  isClossable = true,
  localSelectedOrganization,
  localSelectedOrganizationPermissions,
}) => {
  const entityName = entityType === 'workspaces' ? 'Workspace' : 'Organization';
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const addEntitySchema = useMemo(() => {
    const schema = z.object({
      entityName: z
        .string()
        .min(3)
        .max(50)
        .refine((val) => {
          return !val.includes('-');
        }, `${entityName} name cannot contain -`)
        .refine((val) => {
          return val !== 'dev';
        }, `${entityName} name cannot be "dev"`),
      invitations: z
        .object({
          email: z.string().email().or(z.string().length(0)),
        })
        .array(),
      region: z.string().optional(),
    });

    if (entityType === 'organizations') {
      return schema.extend({
        region: z.string(),
      });
    }

    return schema;
  }, [entityName, entityType]);
  type AddEntitySchema = z.infer<typeof addEntitySchema>;

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    trigger,
  } = useForm<AddEntitySchema>({
    resolver: zodResolver(addEntitySchema),
    defaultValues: { entityName: '', invitations: [{ email: '' }] },
  });

  const [fetcher, isLoading] = useFetcherWithUtils<
    { organizationId: string; workspaceId: string } & FetcherEmptyResponse
  >((fetcherData) => {
    if (entityType === 'organizations') {
      return handleClose(
        entityType,
        searchParams.toString(),
        setClose,
        setSearchParams,
        fetcherData.organizationId,
      );
    }
    handleClose(entityType, searchParams.toString(), setClose, setSearchParams);
    navigate(
      { hash: '', search: `workspaceId=${fetcherData.workspaceId}` },
      { relative: 'route' },
    );
  });

  const onSubmit = (data: AddEntitySchema) => {
    const formData = new FormData();
    formData.append('entityName', data.entityName);
    formData.append('entityType', entityType);
    if (entityType === 'organizations') {
      formData.append('region', data.region as string);
    } else {
      assert(organizationId, 'organizationId is required for work');
      formData.append('organizationId', organizationId);
    }
    fetcher.submit(formData, { method: 'post' });
  };

  const onRegionChanged = async (e: string) => {
    setValue('region', e);
    await trigger('region');
  };

  useEffect(() => {
    if (entityType === 'organizations') {
      if (!searchParams.has('priceId')) {
        setClose();
      }
    }
  }, [entityType, searchParams, setClose]);

  const isCreateDisabled = useMemo(() => {
    if (entityType === 'organizations') return [true, true];
    if (!localSelectedOrganization || !localSelectedOrganizationPermissions)
      return [false, false];
    const policyData: OrganizationPolicyData = {
      selectedOrganization: localSelectedOrganization,
      organizationRole: localSelectedOrganizationPermissions.role,
    };
    return hasPermissionMessageWithPolicy(
      policyData,
      policyData.selectedOrganization.permissions,
      'organization',
      'workspaces',
      'create',
    );
  }, [entityType, localSelectedOrganization, localSelectedOrganizationPermissions]);

  return (
    <>
      <Text className="h-9 font-ubuntu font-bold text-2xl text-[#425565] w-[400px]">
        Create new {entityName.toLowerCase()}
      </Text>
      <form className="scrollbar-thumb-only w-full" onSubmit={handleSubmit(onSubmit)}>
        <TextInput
          {...register('entityName')}
          placeholder={`Provide ${entityName} Name`}
          className={[
            'shadow-none h-11 bg-white border-2  text-[#425565] [&_input]:py-1.5 [&_input]:px-4 rounded-xl font-ubuntu font-normal text-lg placeholder:text-[#9e9e9e] ring-0',
            errors.entityName ? '!border-red' : 'border-[#9e9e9e]',
          ].join(' ')}
          error={!!errors.entityName}
          errorMessage={errors.entityName?.message}
          disabled={isLoading}
        />
        {entityType === 'organizations' && (
          <>
            <Callout
              style={{ margin: '1rem 0' }}
              title="Make up your mind wisely!"
              color="red"
            >
              Settings that are present below cannot be changed later!
            </Callout>

            <SelectBox
              placeholder="Choose organization region"
              onValueChange={onRegionChanged}
              className="[&>button]:ring-0"
            >
              {regions.map(({ region, city }) => (
                <SelectBoxItem key={region} value={region} text={`${region} (${city})`} />
              ))}
            </SelectBox>
            {errors.region && (
              <Text color="red" style={{ color: 'red' }}>
                {errors.region.message}
              </Text>
            )}
          </>
        )}
        {!isCreateDisabled[0] && isCreateDisabled[1] ? (
          <Callout
            style={{ margin: '1rem 0' }}
            title="Workspaces limit reached"
            color="red"
          >
            {isCreateDisabled[1]}
          </Callout>
        ) : null}
        <ModalBottomActions
          cancelDisabled={!isClossable}
          style={{ marginTop: '20px' }}
          setClose={() =>
            handleClose(entityType, searchParams.toString(), setClose, setSearchParams)
          }
          disabled={!isCreateDisabled[0]}
          isLoading={isLoading}
        />
      </form>
    </>
  );
};
